import React, { useMemo } from 'react';

import { AllowedEvent } from '@rbilabs/mparticle-client';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Picture from 'components/picture';
import { Calories } from 'components/price-and-calories';
import useEffectOnce from 'hooks/use-effect-once';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useUIContext } from 'state/ui';
import { RBIBrand, brand } from 'utils/environment';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

import { CartItemButton, CartItemIcon } from './styles';
import { ICartItemProps } from './types';

const CartItemPicture = styled(Picture)`
  width: 70px;
  height: 50px;
  margin-right: 8px;
`;

const CartItemInfo = styled.div`
  font-size: 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CartItemName = styled.div`
  font-size: 15px;
`;

const CartItemDetails = styled.div`
  text-align: left;
`;

const CartItemPrice = styled.div`
  font-weight: bold;
  display: inline;
`;

const CartItemCalories = styled.div`
  color: #626262;
  padding-left: 5px;
  display: inline;
`;

const CartItem = ({ item, addToCard, logEventData }: ICartItemProps) => {
  const { logEventName } = logEventData;
  const mParticle = useMParticleContext();
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const { pricingFunction } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const formatJoules = useFormatJoules();
  const itemCalories = item.nutrition?.calories;

  const price = useMemo(
    () => formatCurrencyForLocale(pricingFunction(item)),
    [item, pricingFunction, formatCurrencyForLocale]
  );

  const caloriesInJoules = formatJoules(itemCalories);

  const calories = useMemo(
    () => formatCalories(itemCalories, 'calories'),
    [formatCalories, itemCalories]
  );

  const euFormattedNutritionText = formatMessage(
    { id: 'euNutritionValues' },
    { calories, joules: caloriesInJoules }
  );

  const globalizedCalorieFormat = calories ? `${euFormattedNutritionText}` : '';
  const finalCalories = enableEUNutritionDisplay ? globalizedCalorieFormat : calories;

  useEffectOnce(() => {
    if (brand() === RBIBrand.TH) {
      mParticle.logEvent(logEventName, EventTypes.Other, {
        Id: item._id,
        Name: item.name?.locale,
        Price: pricingFunction(item) / 100,
      });
    }

    const eventData = {
      name: logEventName,
      type: EventTypes.Other,
      attributes: {
        name: item.name?.locale,
        sanityId: item._id,
        price: pricingFunction(item) / 100,
      },
    } as AllowedEvent;

    mParticle.logRBIEvent(eventData);
  });

  return (
    <CartItemButton
      data-testid="cart-add-to-order-btn"
      aria-label={formatMessage({ id: 'addItemLabel' }, { itemName: item.name?.locale })}
      onClick={() => {
        hapticImpact({ style: HapticsImpactStyle.Heavy });

        addToCard(item);
      }}
    >
      <CartItemInfo
        aria-label={formatMessage(
          { id: 'cartItemLabel' },
          {
            itemName: item.name?.locale,
            calories: formatCalories(itemCalories),
            price,
          }
        )}
      >
        <CartItemPicture image={item.image} alt={item.name?.locale} />
        <CartItemDetails
          tabIndex={0}
          aria-labelledby={`add-to-order-header cart-name-${item._id} cart-price-${item._id} cart-calories-${item._id}`}
        >
          <CartItemName id={`cart-name-${item._id}`}>{item.name?.locale}</CartItemName>
          <CartItemPrice id={`cart-price-${item._id}`}>
            <span data-testid={`cart-price-text-${item._id}`}>{price}</span>
          </CartItemPrice>
          <Calories>
            {' '}
            |<CartItemCalories id={`cart-calories-${item._id}`}>{finalCalories}</CartItemCalories>
          </Calories>
        </CartItemDetails>
      </CartItemInfo>
      <CartItemIcon>+</CartItemIcon>
    </CartItemButton>
  );
};

export default CartItem;
